import { call, put, take } from 'redux-saga/effects'
import { callApi } from '../../api/api'
import { push } from 'connected-react-router'
import {
  storeTokens,
  getRefreshToken,
  getDecodedAccessToken,
  isAccessTokenExpired
} from '../../util/webStorageUtils'
import { currentUserSuccess } from './user'
import { logoutRoute } from '../../routes/routes'

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET

// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const REFRESH = 'foodsby/token/REFRESH'
export const REFRESH_SUCCESS = 'foodsby/token/REFRESH_SUCCESS'
export const REFRESH_FAILURE = 'foodsby/token/REFRESH_FAILURE'

let isRefreshing = false
export const refreshStart = args => {
  isRefreshing = true
  return {
    type: REFRESH,
    payload: { args },
    refreshing: isRefreshing
  }
}

export const refreshSuccess = (response, args) => {
  isRefreshing = false
  return {
    type: REFRESH_SUCCESS,
    payload: { response, args },
    refreshing: isRefreshing
  }
}

export const refreshFailure = error => {
  isRefreshing = false
  return {
    type: REFRESH_FAILURE,
    error,
    refreshing: isRefreshing
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [REFRESH]: (state, action) => {
    const { args } = action.payload
    return {
      ...state,
      args,
      refreshing: true
    }
  },
  [REFRESH_SUCCESS]: (state, action) => {
    const { response } = action.payload

    storeTokens(response)

    return {
      ...state,
      error: undefined,
      refreshing: undefined
    }
  },
  [REFRESH_FAILURE]: (state, action) => {
    return {
      ...state,
      error: action.error,
      refreshing: undefined
    }
  }
}

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  refreshing: undefined
}

export default function token(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

/**
 *  Checks if the access token needs to be refreshed by comparing the expiration
 *  date with the current date.
 *  @return  {Bool}
 */
export function needRefresh() {
  return isAccessTokenExpired()
}

export function* refreshTokenSaga() {
  // If the application is already waiting for a new set of tokens, wait for the
  // completion of that request instead of creating a new one.
  if (isRefreshing) {
    const { error } = yield take([REFRESH_SUCCESS, REFRESH_FAILURE])
    if (error) {
      yield put(push(logoutRoute.path))
    }
    return error
  }

  // dispatch an action indicating that the application is 'refreshing' the tokens
  yield put(refreshStart())

  try {
    // refresh token and store them in localStorage
    const { data } = yield call(refreshToken)
    yield call(storeTokens, data)

    const token = yield call(getDecodedAccessToken)
    if (token) {
      yield put(
        currentUserSuccess({
          userId: token.user_id,
          userName: token.user_name,
          email: token.user_name,
          deliveryLocationId: token.location_id
        })
      )

      // dispatch success message so the 'refreshing' flag is reset
      yield put(refreshSuccess())
      return null
    } else {
      yield put(refreshFailure())
    }
  } catch (err) {
    yield put(refreshFailure(err))
    return err
  }
}

export function refreshToken() {
  const token = getRefreshToken()
  if (token) {
    let data = new FormData()
    data.append('grant_type', 'refresh_token')
    data.append('refresh_token', token)

    return callApi.post(`/api/v1/uaa/oauth/token`, data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      auth: {
        username: CLIENT_ID,
        password: CLIENT_SECRET
      }
    })
  } else {
    // this will trigger refresh failure which is desired
    throw Error('No refresh token found.')
  }
}
